"use client";

import {
	type PublicFeedbackFormData,
	PublicFeedbackSchema,
} from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

type PublicFeedbackProps = {
	/** Trigger element that opens the dialog */
	trigger: React.ReactNode;
	/** Optional title for the dialog */
	title?: string;
	/** Optional description for the dialog */
	description?: string;
	/** Optional path to include with feedback */
	path?: string;
	/** Callback when feedback is successfully submitted */
	onSuccess?: () => void;
	/** Callback when dialog should close */
	onOpenChange?: (open: boolean) => void;
	/** Whether the dialog is open */
	open?: boolean;
	/** Custom submit button text */
	submitButtonText?: string;
	/** Custom placeholder for message textarea */
	messagePlaceholder?: string;
	/** Whether to show email field */
	showEmail?: boolean;
};

export function PublicFeedback({
	trigger,
	title = "Submit Feedback",
	description = "Tell me what broke, what confused you, or what you wish existed. I run this alone, so I actually read these.",
	path,
	onSuccess,
	onOpenChange,
	open: controlledOpen,
	submitButtonText = "Submit Feedback",
	messagePlaceholder = "What's on your mind?",
	showEmail = true,
}: PublicFeedbackProps) {
	const [internalOpen, setInternalOpen] = useState(false);
	const open = controlledOpen ?? internalOpen;
	const setOpen = onOpenChange ?? setInternalOpen;

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<PublicFeedbackFormData>({
		resolver: zodResolver(PublicFeedbackSchema),
		defaultValues: {
			email: "",
			message: "",
		},
	});

	const onSubmit = async (data: PublicFeedbackFormData) => {
		try {
			const currentPath =
				path ?? window.location.pathname + window.location.search;
			const response = await requestWithHandling<void>(() =>
				apiClient.api.publicFeedbackCreate({
					message: data.message,
					email:
						data.email && data.email.trim() !== "" ? data.email : undefined,
					path: currentPath,
				}),
			);

			if (response.code !== "SUCCESS") {
				throw new Error(response.message);
			}

			toast.success("Feedback submitted", {
				description: "Thanks—I read every one of these.",
			});

			reset();
			setOpen(false);
			onSuccess?.();
		} catch (error) {
			toast.error("Failed to submit feedback", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(newOpen) => {
				setOpen(newOpen);
				if (!newOpen) {
					reset();
				}
			}}
		>
			<DialogTrigger>{trigger}</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
					<div className="space-y-4">
						{showEmail && (
							<div className="space-y-1">
								<Input
									type="email"
									placeholder="Email (optional)"
									{...register("email")}
									aria-invalid={errors.email ? "true" : "false"}
								/>
								{errors.email && (
									<p className="text-sm text-red-500">{errors.email.message}</p>
								)}
							</div>
						)}
						<div className="space-y-1">
							<Textarea
								placeholder={messagePlaceholder}
								{...register("message")}
								aria-invalid={errors.message ? "true" : "false"}
							/>
							{errors.message && (
								<p className="text-sm text-red-500">{errors.message.message}</p>
							)}
						</div>
					</div>
					<DialogFooter>
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? "Submitting..." : submitButtonText}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
